import { ADMIN_ROUTE_PERMISSIONS, isMenuItemVisible, type MenuPermission } from './menu-access.ts';

export type AdminMenuPageDef = {
  key: string;
  label: string;
  permission: MenuPermission;
};

export type AdminMenuGroupDef = {
  key: string;
  label: string;
  pages: readonly AdminMenuPageDef[];
};

/** 侧栏模块 / 页面顺序的唯一来源。AdminLayout 只负责挂图标。 */
export const ADMIN_MENU_STRUCTURE: readonly AdminMenuGroupDef[] = [
  {
    key: 'data',
    label: '数据中心',
    pages: [
      { key: '/', label: '数据概览', permission: ADMIN_ROUTE_PERMISSIONS.dashboard },
      {
        key: '/dashboard/trends',
        label: '数据趋势',
        permission: ADMIN_ROUTE_PERMISSIONS.dashboardTrends,
      },
      {
        key: '/operation-analytics',
        label: '运营分析',
        permission: ADMIN_ROUTE_PERMISSIONS.dashboardAnalytics,
      },
      {
        key: '/dashboard/model-usage',
        label: '模型用量',
        permission: ADMIN_ROUTE_PERMISSIONS.dashboardModelUsage,
      },
    ],
  },
  {
    key: 'user',
    label: '用户中心',
    pages: [
      { key: '/users', label: '用户管理', permission: ADMIN_ROUTE_PERMISSIONS.users },
      { key: '/invite-codes', label: '邀请码管理', permission: 'invite_codes:list' },
      { key: '/account-appeals', label: '账号申诉审核', permission: 'account_appeals:list' },
    ],
  },
  {
    key: 'tenant',
    label: '租户管理',
    pages: [
      {
        key: '/organizations',
        label: '组织管理',
        permission: ADMIN_ROUTE_PERMISSIONS.organizations,
      },
      { key: '/certifications', label: '企业认证', permission: 'certifications:list' },
      {
        key: '/org-dissolution-requests',
        label: '企业解散审核',
        permission: 'org_dissolution_requests:list',
      },
    ],
  },
  {
    key: 'content',
    label: '内容管理',
    pages: [
      { key: '/projects', label: '项目管理', permission: 'content:projects:list' },
      {
        key: '/project-exports',
        label: '导出任务',
        permission: ADMIN_ROUTE_PERMISSIONS.projectExports,
      },
      { key: '/screenplays', label: '剧本管理', permission: 'content:screenplays:list' },
      { key: '/storyboards', label: '分镜管理', permission: 'content:storyboards:list' },
      { key: '/visual-styles', label: '视觉风格', permission: 'content:visual_styles:list' },
      { key: '/ai-prompt-commands', label: '指令提示词', permission: 'ai_prompt_commands:list' },
    ],
  },
  {
    key: 'ai',
    label: 'AI 管理',
    pages: [
      { key: '/ai-tasks', label: '任务监控', permission: 'ai_tasks:list' },
      { key: '/ai-assistant-runs', label: '助手运行', permission: 'ai_assistant_runs:list' },
      { key: '/ai-error-rules', label: '错误文案', permission: 'ai_error_rules:list' },
      { key: '/task-queue-monitor', label: '队列监控', permission: 'task_queue:monitor:list' },
      { key: '/ai-usage', label: '用量统计', permission: ADMIN_ROUTE_PERMISSIONS.aiUsage },
      { key: '/ai-skills', label: '技能管理', permission: 'ai_skills:list' },
      { key: '/ai-config', label: 'AI 配置', permission: ADMIN_ROUTE_PERMISSIONS.aiConfig },
      { key: '/knowledge', label: '知识库', permission: 'knowledge:list' },
    ],
  },
  {
    key: 'provider-hub',
    label: '模型中心',
    pages: [
      { key: '/provider-hub', label: '运营总览', permission: 'provider_hub:models:list' },
      { key: '/provider-hub/models', label: '模型定义', permission: 'provider_hub:models:list' },
      {
        key: '/provider-hub/providers',
        label: '厂商管理',
        permission: 'provider_hub:providers:list',
      },
      {
        key: '/provider-hub/accounts',
        label: '账号管理',
        permission: 'provider_hub:accounts:list',
      },
      { key: '/provider-hub/routes', label: '路由管理', permission: 'provider_hub:routes:list' },
      { key: '/provider-hub/monitor', label: '并发监控', permission: 'provider_hub:monitor:list' },
      {
        key: '/provider-hub/cost-pricing',
        label: '成本价格',
        permission: 'provider_hub:pricing:list',
      },
      {
        key: '/provider-hub/health-events',
        label: '健康事件',
        permission: 'provider_hub:monitor:list',
      },
    ],
  },
  {
    key: 'finance',
    label: '财务中心',
    pages: [
      { key: '/credits', label: '算力管理', permission: ADMIN_ROUTE_PERMISSIONS.credits },
      {
        key: '/recharge-orders',
        label: '充值订单',
        permission: ADMIN_ROUTE_PERMISSIONS.rechargeOrders,
      },
      {
        key: '/credit-packages',
        label: '套餐管理',
        permission: ADMIN_ROUTE_PERMISSIONS.creditPackages,
      },
      {
        key: '/credit-pricing',
        label: '定价配置',
        permission: ADMIN_ROUTE_PERMISSIONS.creditPricing,
      },
    ],
  },
  {
    key: 'monitoring',
    label: '统计管理',
    pages: [
      { key: '/frontend-error-issues', label: '前端错误', permission: 'monitoring:errors:list' },
      { key: '/analytics-overview', label: '行为分析', permission: 'monitoring:analytics:list' },
    ],
  },
  {
    key: 'system',
    label: '系统设置',
    pages: [
      { key: '/admins', label: '账号管理', permission: ADMIN_ROUTE_PERMISSIONS.admins },
      { key: '/roles', label: '角色权限', permission: ADMIN_ROUTE_PERMISSIONS.roles },
      { key: '/permissions', label: '权限列表', permission: ADMIN_ROUTE_PERMISSIONS.permissions },
      { key: '/audit-logs', label: '审计日志', permission: 'audit_logs:list' },
      {
        key: '/system-config',
        label: '系统配置',
        permission: ADMIN_ROUTE_PERMISSIONS.systemConfig,
      },
    ],
  },
];

export function toLoginLandingCandidates(
  structure: readonly AdminMenuGroupDef[] = ADMIN_MENU_STRUCTURE
): ReadonlyArray<{ key: string; permission: MenuPermission }> {
  return structure.flatMap((group) =>
    group.pages.map((page) => ({ key: page.key, permission: page.permission }))
  );
}

export function firstAllowedMenuPath(granted: readonly string[]): string {
  const match = toLoginLandingCandidates().find((item) =>
    isMenuItemVisible(item.permission, granted)
  );
  return match?.key ?? '/';
}

export function toPermissionModuleOrder(
  structure: readonly { key: string }[] = ADMIN_MENU_STRUCTURE
): readonly string[] {
  return structure.map((group) => group.key);
}
